import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { activeProfileRoles, isGuardOnlyProfile } from '../lib/guardProfile';
import { Loader2, ShieldX } from 'lucide-react';
import { Button } from './ui/button';

// Mapeo de rutas a modulos
const RUTA_MODULO_MAP = {
  '/asistencia': 'asistencia',
  '/calendario-operativo': 'calendario_operativo',
  '/checklists': 'checklists',
  '/incidencias': 'incidencias',
  '/documentos': 'documentos',
  '/alertas': 'alertas',
  '/financiero': 'financiero',
  '/caja-chica': 'caja_chica',
  '/acuerdos': 'acuerdos',
  '/minutas': 'minutas',
  '/reportes': 'reportes',
  '/proveedores': 'proveedores',
  '/atencion-vecinal': 'atencion_vecinal',
  '/access-control': 'access_control',
  '/ares-seguridad': 'access_control',
  '/sanciones': 'sanciones',
};

export const ProtectedRoute = ({ children, requiredRoles = [], requiredModulo = null }) => {
  const {
    user,
    loading,
    isAuthenticated,
    isDireccion,
    getDefaultCondominioId,
    getAvailableModulos,
  } = useAuth();
  const location = useLocation();

  const moduloFromPath = Object.entries(RUTA_MODULO_MAP).find(
    ([path]) => location.pathname.startsWith(path)
  )?.[1];

  const moduloRequerido = requiredModulo || moduloFromPath;
  const activeCondominioId =
    getDefaultCondominioId?.() ||
    user?.condominios_ids?.[0] ||
    null;
  const userRoles = activeProfileRoles(user);
  const hasActiveRoles = userRoles.length > 0;
  const isGuardOnly = isGuardOnlyProfile(user);
  const isCouncilOnly = userRoles.includes('consejo') && !userRoles.some((role) => ['direccion', 'supervisor', 'administrador'].includes(role));

  const modulePermissions =
    activeCondominioId && user?.effective_permissions?.by_condominio?.[activeCondominioId]?.modulos
      ? user.effective_permissions.by_condominio[activeCondominioId].modulos
      : {};
  const availableModulos = getAvailableModulos?.() || [];

  const visibleInActiveCondo = moduloRequerido
    ? Boolean(modulePermissions?.[moduloRequerido]?.can_view)
    : true;
  const visibleInAnyCondo = moduloRequerido
    ? Array.isArray(availableModulos) && availableModulos.includes(moduloRequerido)
    : true;

  const shouldBlockModule =
    Boolean(moduloRequerido) &&
    !isDireccion() &&
    !(activeCondominioId ? visibleInActiveCondo : visibleInAnyCondo);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#7A1F2B]" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isGuardOnly && !location.pathname.startsWith('/ares-seguridad') && !location.pathname.startsWith('/access-control')) {
    return <Navigate to="/ares-seguridad/caseta" replace />;
  }

  if (isCouncilOnly && ['/dashboard', '/asistencia'].some((path) => location.pathname.startsWith(path))) {
    return <Navigate to="/consejo" replace />;
  }

  if (!isDireccion() && !hasActiveRoles) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-8">
          <div className="mb-6 p-4 bg-amber-50 rounded-full inline-block">
            <ShieldX className="h-12 w-12 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Usuario pendiente de asignación
          </h2>
          <p className="text-gray-600 mb-6">
            Tu cuenta ya fue creada, pero todavía no tiene roles ni condominios activos asignados.
            Dirección debe completar tu acceso antes de que puedas operar en la plataforma.
          </p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Recargar
          </Button>
        </div>
      </div>
    );
  }

  if (requiredRoles.length > 0) {
    const hasRequiredRole =
      requiredRoles.some((role) => userRoles.includes(role)) || user?.is_direccion;

    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (shouldBlockModule) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-8">
          <div className="mb-6 p-4 bg-red-50 rounded-full inline-block">
            <ShieldX className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Módulo no disponible
          </h2>
          <p className="text-gray-600 mb-6">
            El módulo <strong className="text-gray-900">{moduloRequerido}</strong> no está
            disponible para tu usuario en este momento.
          </p>
          <Button onClick={() => window.history.back()} variant="outline">
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
